import React from 'react';
import { Box, Typography, Stack, Switch, FormControlLabel, Select, MenuItem, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { LayerVisibility } from '../canvas/types';

interface OverlayPanelProps {
    layerVisibility: LayerVisibility;
    setLayerVisibility: React.Dispatch<React.SetStateAction<LayerVisibility>>;
}

export function OverlayPanel({ layerVisibility, setLayerVisibility }: OverlayPanelProps) {
    const { t } = useTranslation();

    const handleToggle = (key: keyof LayerVisibility) => {
        setLayerVisibility(prev => ({ ...prev, [key]: !prev[key as keyof LayerVisibility] }));
    };

    return (
        <Stack spacing={2} sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
                {t('map_creator.overlay.visibility')}
            </Typography>

            <FormControlLabel
                control={<Switch checked={layerVisibility.symbols} onChange={() => handleToggle('symbols')} size="small" />}
                label={<Typography variant="body2">{t('map_creator.overlay.symbols')}</Typography>}
            />
            
            <FormControlLabel
                control={<Switch checked={layerVisibility.rivers} onChange={() => handleToggle('rivers')} size="small" />}
                label={<Typography variant="body2">{t('map_creator.overlay.rivers')}</Typography>}
            />

            <FormControlLabel
                control={<Switch checked={layerVisibility.paths} onChange={() => handleToggle('paths')} size="small" />}
                label={<Typography variant="body2">{t('map_creator.overlay.paths')}</Typography>}
            />

            <FormControlLabel
                control={<Switch checked={layerVisibility.areas} onChange={() => handleToggle('areas')} size="small" />}
                label={<Typography variant="body2">{t('map_creator.overlay.areas')}</Typography>}
            />

            <FormControlLabel
                control={<Switch checked={layerVisibility.labels} onChange={() => handleToggle('labels')} size="small" />}
                label={<Typography variant="body2">{t('map_creator.overlay.labels')}</Typography>}
            />

            <FormControlLabel
                control={<Switch checked={layerVisibility.pinpoints} onChange={() => handleToggle('pinpoints')} size="small" />}
                label={<Typography variant="body2">{t('map_creator.overlay.pinpoints')}</Typography>}
            />

            <Typography variant="overline" color="text.secondary" fontWeight="bold" sx={{ mt: 2 }}>
                {t('map_creator.overlay.grid')}
            </Typography>

            <FormControlLabel
                control={<Switch checked={layerVisibility.grid} onChange={() => handleToggle('grid')} size="small" />}
                label={<Typography variant="body2">{t('map_creator.overlay.showGrid')}</Typography>}
            />

            {layerVisibility.grid && (
                <Box sx={{ pl: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                        {t('map_creator.overlay.gridType')}
                    </Typography>
                    <Select
                        fullWidth
                        size="small"
                        value={layerVisibility.gridType}
                        onChange={(e) => setLayerVisibility(prev => ({ ...prev, gridType: e.target.value as 'square' | 'hex' }))}
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="square">{t('map_creator.overlay.square')}</MenuItem>
                        <MenuItem value="hex">{t('map_creator.overlay.hex')}</MenuItem>
                    </Select>

                    <Typography variant="caption" color="text.secondary" gutterBottom>
                        {t('map_creator.overlay.gridSize')}
                    </Typography>
                    <Slider
                        size="small"
                        value={layerVisibility.gridSize}
                        min={20}
                        max={200}
                        step={10}
                        onChange={(_, val) => setLayerVisibility(prev => ({ ...prev, gridSize: val as number }))}
                        valueLabelDisplay="auto"
                    />
                </Box>
            )}
        </Stack>
    );
}
